import { useFileContext } from "api/files/FilesProvider";
import { QUERY_STATUS } from "constants/constants";
import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  parseInputDate,
  formatDateForAPI,
  isCreateToDisplayNavigation,
} from "./helper";
import { formatValueDate, getJobsInCategories } from "./functions";
import { useJobCategoryContext } from "api/settings/job-category/JobCategoryProvider";

// Category <-> Job cascade of the two entry sheets. Both drive the same model:
// a scalar category, a job held as a single tag, and react-hook-form. The
// filter screens deliberately do not use this — their cardinality and their
// state differ (see AbaqueToolsForm, MissionToolsForm, SharedDashboardForm).
//
// Since redmine 12940 the category is DERIVED, never entered: a job belongs to
// exactly one category, and neither the mission nor the job history line stores
// one any more. The field is kept on screen for two reasons — it narrows a job
// list that went from 34 to 80 entries, and it shows the user which category
// their job falls under — but it is never part of a payload.
export const useJobCategoryCascade = ({
  watch,
  setValue,
  clearErrors,
  jobs,
}) => {
  const {
    getCategoryJobIds,
    getJobCategoryId,
    jobCategoryList,
    jobCategoryListGetter,
  } = useJobCategoryContext();

  const domain_id = watch("domain_id", []);
  const domain_category_id = watch("domain_category_id", "");

  useEffect(() => {
    jobCategoryListGetter();
  }, [jobCategoryListGetter]);

  // Jobs offered are restricted to the selected category — strictly: none
  // offered while none is picked, which is exactly when the field is locked.
  const allowedJobIds = domain_category_id
    ? getCategoryJobIds(domain_category_id)
    : [];
  const jobOptions = getJobsInCategories(jobs, allowedJobIds);

  // Changing category clears a job that no longer belongs to it — removing the
  // category included, which leaves no job selectable at all (redmine 12939).
  // Keyed on the category alone on purpose: adding domain_id here would clear
  // the job the moment it is picked, fighting the auto-fill effect below.
  //
  // The ref is what separates a real change from the empty first render of a
  // sheet still hydrating: the job arrives from the API before the category is
  // derived from it, so acting on that first pass would wipe the job the sheet
  // just received.
  //
  // The job ref does the same for a sheet hydrated again (HF-0356): the sheet
  // re-runs its init on late data and writes the job together with the empty
  // category default. A user only ever changes one of the two, so a job that
  // changed in the same pass is a re-hydration, not a category change.
  const previousCategoryRef = useRef(undefined);
  const previousJobRef = useRef(domain_id);
  useEffect(() => {
    const previousCategory = previousCategoryRef.current;
    previousCategoryRef.current = domain_category_id;
    if (
      previousCategory === undefined ||
      previousCategory === domain_category_id ||
      previousJobRef.current !== domain_id
    ) {
      return;
    }
    const selectedJobId = domain_id?.[0]?.value;
    if (!selectedJobId) return;
    // Never prune against a referential that has not landed: it would read as
    // "this category offers nothing" and wipe a job the sheet legitimately holds.
    if (domain_category_id && !jobCategoryList?.length) return;
    if (!allowedJobIds.includes(selectedJobId)) {
      setValue("domain_id", []);
    }
  }, [domain_category_id]);

  // Declared after the pruning effect so it still reads the previous job.
  useEffect(() => {
    previousJobRef.current = domain_id;
  }, [domain_id]);

  // The selected job dictates the category — including when one is already set,
  // since the sheet no longer receives it from the API and the referential is the
  // only source. Depends on the getter too: the referential often lands after the
  // sheet is hydrated, and the effect has to run again once it does.
  useEffect(() => {
    const selectedJobId = domain_id?.[0]?.value;
    if (!selectedJobId) return;
    const categoryId = getJobCategoryId(selectedJobId);
    if (categoryId && categoryId !== domain_category_id) {
      setValue("domain_category_id", categoryId);
      clearErrors?.("domain_category_id");
    }
  }, [domain_id, getJobCategoryId]);

  return { domain_category_id, domain_id, jobCategoryList, jobOptions };
};

export const useUpdateDateValue = (lang) => {
  const updateDateValue = useCallback(
    (value, inputValue, setSelectedDate, setInputValue) => {
      if (!inputValue) {
        if (value) {
          const date = new Date(value);
          setSelectedDate(date);
          setInputValue(formatValueDate({ date, lang }));
        } else {
          setSelectedDate(null);
          setInputValue("");
        }
      }
    },
    [lang]
  );
  return { updateDateValue };
};

export const useDatePickerOutsideClick = (isOpen, containerRef, onClose) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    const handleEscapeKey = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    const handleScroll = (e) => {
      if (
        e.target.closest(".react-datepicker__year-select") ||
        e.target.closest(".react-datepicker__month-select")
      ) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
      document.addEventListener("wheel", handleScroll, { passive: false });
      document.addEventListener("touchmove", handleScroll, { passive: false });
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
      document.removeEventListener("wheel", handleScroll);
      document.removeEventListener("touchmove", handleScroll);

      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);
};

export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowSize;
};

export const useDateValidation = () => {
  const validateAndFormatDate = (
    formattedValue,
    lang,
    onValidDate,
    onInvalidYear
  ) => {
    if (formattedValue.length !== 10) return;

    const year = parseInt(formattedValue.substring(6, 10), 10);

    // Validate year range (2000-2050)
    if (year < 2000 || year > 2050) {
      onInvalidYear(formattedValue.substring(0, 6));
      return;
    }

    // Year is valid, parse the date
    const parsedDate = parseInputDate(formattedValue, lang);
    if (parsedDate) {
      onValidDate(parsedDate);
    }
  };

  return { validateAndFormatDate };
};

const MIN_LOADER_DURATION = 2000; // 2 seconds minimum to avoid flickering

export const useProfilePictureHandler = () => {
  const {
    fileById,
    createdFile,
    getFileById,
    setFileById,
    loadStatus,
    setLoadStatus,
  } = useFileContext();
  const [picture, setPicture] = useState();
  // Local state to manage the minimum loader delay
  const [displayLoadStatus, setDisplayLoadStatus] = useState(QUERY_STATUS.INITIAL);
  const loaderStartTimeRef = useRef(null);

  useEffect(() => {
    setPicture(fileById.length > 0 ? `data:image/png;base64,${fileById}` : "");
  }, [fileById]);

  useEffect(() => {
    // Do nothing if createdFile is empty - the image may come from another source
    // (e.g.: useEntityProfilePicture which loads via entityPictureFileId)
    const isEmpty =
      !createdFile ||
      Object.keys(createdFile).length === 0 ||
      (!createdFile?.picture_file_id &&
        (!createdFile?.uri || createdFile?.uri === ""));

    if (isEmpty) {
      return;
    }

    const hasValidPictureFileId =
      createdFile?.picture_file_id && createdFile.picture_file_id.trim() !== "";

    const hasValidUri = createdFile?.uri && createdFile.uri.trim() !== "";

    if (!hasValidPictureFileId && !hasValidUri) {
      return;
    }

    const fileId = createdFile?.picture_file_id || createdFile?.uri;
    getFileById({ id: fileId, isBase64Request: true });
  }, [createdFile]);

  // Manage the minimum loader delay to avoid flickering
  useEffect(() => {
    if (loadStatus === QUERY_STATUS.LOADING) {
      loaderStartTimeRef.current = Date.now();
      setDisplayLoadStatus(QUERY_STATUS.LOADING);
    } else if (loadStatus === QUERY_STATUS.SUCCESS || loadStatus === QUERY_STATUS.ERROR) {
      const elapsed = Date.now() - (loaderStartTimeRef.current || 0);
      const remaining = Math.max(0, MIN_LOADER_DURATION - elapsed);

      if (remaining > 0) {
        const timer = setTimeout(() => {
          setDisplayLoadStatus(loadStatus);
          setLoadStatus(QUERY_STATUS.INITIAL);
        }, remaining);
        return () => clearTimeout(timer);
      } else {
        setDisplayLoadStatus(loadStatus);
        setLoadStatus(QUERY_STATUS.INITIAL);
      }
    }
  }, [loadStatus]);

  return { picture, loadStatus: displayLoadStatus };
};

const usePictureStateCleanup = () => {
  const location = useLocation();
  const { setFileById, setCreatedFile } = useFileContext();
  const [currentPath, setCurrentPath] = useState(location.pathname);

  const cleanupState = () => {
    setFileById("");
    setCreatedFile({});
  };

  const shouldCleanup = (oldPath, newPath) => {
    // Don't cleanup when navigating from create to display of same entity type
    if (isCreateToDisplayNavigation(oldPath, newPath)) {
      return false;
    }

    // Don't cleanup when only the scope changes (same entity type and ID)
    // e.g., /admin/staff/123 -> /manager/staff/123
    const oldParts = oldPath.split("/").filter(Boolean);
    const newParts = newPath.split("/").filter(Boolean);

    if (oldParts.length >= 3 && newParts.length >= 3) {
      // Compare entity type (staff, consultant, etc.) and ID
      const oldEntity = oldParts.slice(1).join("/");
      const newEntity = newParts.slice(1).join("/");
      if (oldEntity === newEntity) {
        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    if (currentPath !== location.pathname) {
      if (shouldCleanup(currentPath, location.pathname)) {
        cleanupState();
      }
      setCurrentPath(location.pathname);
    }
  }, [location.pathname, currentPath]);

  return { cleanupState };
};

export const useProfileUploadSetup = () => {
  const { createdFile, setCreatedFile, createFile, uploadProfilePicture } =
    useFileContext();
  const { picture } = useProfilePictureHandler();
  const [tempUserId, setTempUserId] = useState(null);
  const { cleanupState } = usePictureStateCleanup();

  useEffect(() => {
    cleanupState();
  }, []);

  useEffect(() => {
    if (!tempUserId) {
      setTempUserId(
        `temp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
      );
    }
  }, [tempUserId]);

  return {
    picture,
    tempUserId,
    createdFile,
    setCreatedFile,
    createFile,
    uploadProfilePicture,
  };
};

export const useEntityProfilePicture = (entityPictureFileId) => {
  const { getFileById, setFileById } = useFileContext();
  const { picture } = useProfilePictureHandler();
  const { cleanupState } = usePictureStateCleanup();

  useEffect(() => {
    if (entityPictureFileId && entityPictureFileId.trim() !== "") {
      cleanupState();
      getFileById({ id: entityPictureFileId, isBase64Request: true });
    } else {
      setFileById("");
    }
  }, [entityPictureFileId]);

  return { picture };
};

const formatDateChange = (date, lang, label, onChange, setInputValue) => {
  const formattedDate = formatValueDate({ date, lang });
  setInputValue(formattedDate);

  const apiFormattedDate = formatDateForAPI(date);
  onChange &&
    onChange(
      label ? { target: { value: apiFormattedDate } } : apiFormattedDate
    );

  return formattedDate;
};

export const createDatePickerHandler =
  (setSelectedDate, setInputValue, onChange, setShowDatePicker, lang, label) =>
    (date) => {
      setSelectedDate(date);

      if (date) {
        formatDateChange(date, lang, label, onChange, setInputValue);
      }

      setShowDatePicker(false);
    };

export const useDatePickerChangeHandler = (
  inputConfigs,
  lang,
  label,
  selectedDateFrom,
  selectedDateTo,
  onChangeFrom,
  onChangeTo,
  setShowDatePickerFrom,
  setShowDatePickerTo
) => {
  const handleDateChange = useCallback(
    (dateType, date) => {
      const config = inputConfigs[dateType];
      const isFromDate = dateType === "from";
      const onChange = isFromDate ? onChangeFrom : onChangeTo;
      const setShowDatePicker = isFromDate
        ? setShowDatePickerFrom
        : setShowDatePickerTo;

      const handler = createDatePickerHandler(
        config.setters.setSelectedDate,
        config.setters.setInputValue,
        onChange,
        setShowDatePicker,
        lang,
        label
      );

      return handler(date);
    },
    [
      inputConfigs,
      lang,
      label,
      selectedDateFrom,
      selectedDateTo,
      onChangeFrom,
      onChangeTo,
      setShowDatePickerFrom,
      setShowDatePickerTo,
    ]
  );

  return {
    handleDateChange,
  };
};

export const useDismiss = ({
  open,
  onDismiss,
  ref,
  escape = true,
  outsideClick = true,
}) => {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onDismiss();
    };
    const handleClickOutside = (event) => {
      if (ref?.current && !ref.current.contains(event.target)) onDismiss();
    };

    if (escape) document.addEventListener("keydown", handleKeyDown);
    if (outsideClick)
      document.addEventListener("mousedown", handleClickOutside);

    return () => {
      if (escape) document.removeEventListener("keydown", handleKeyDown);
      if (outsideClick)
        document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onDismiss, ref, escape, outsideClick]);
};
